import { Controller } from "@hotwired/stimulus"
import * as bootstrap from "bootstrap"

export default class extends Controller {
  static targets = ["list", "emptyState", "countBadge", "addModal", "editModal",
    "addForm", "editForm", "addErrors", "editErrors", "addErrorList",
    "editErrorList"]

  static values = {
    apiBaseUrl: String,
    context: String,
    permissions: Array,
    maxDependents: Number,
  }

  connect() {
    console.log("Dependents widget connected", this.data.get("context"))

    // Initialize Bootstrap modals
    if (this.hasAddModalTarget) {
      this.addModal = new bootstrap.Modal(this.addModalTarget)
    }

    if (this.hasEditModalTarget) {
      this.editModal = new bootstrap.Modal(this.editModalTarget)
    }

    // Bind form submit handlers
    if (this.hasAddFormTarget) {
      this.addFormTarget.addEventListener('submit', this.handleAddSubmit.bind(this))
    }

    if (this.hasEditFormTarget) {
      this.editFormTarget.addEventListener('submit', this.handleEditSubmit.bind(this))
    }

    // Listen for Turbo Stream events for delete operations
    document.addEventListener('turbo:before-stream-render', this.handleTurboStream.bind(this))

    this.updateUI()
  }

  disconnect() {
    if (this.hasAddFormTarget) {
      this.addFormTarget.removeEventListener('submit', this.handleAddSubmit.bind(this))
    }

    if (this.hasEditFormTarget) {
      this.editFormTarget.removeEventListener('submit', this.handleEditSubmit.bind(this))
    }

    document.removeEventListener('turbo:before-stream-render', this.handleTurboStream.bind(this))
  }

  // Public action methods
  openAddModal() {
    if (!this.canCreate()) {
      this.showError('You do not have permission to add dependents')
      return
    }

    if (this.hasReachedMaxDependents()) {
      this.showError(`Maximum of ${this.maxDependentsValue} dependents allowed`)
      return
    }

    this.clearErrors('add')
    this.addFormTarget.reset()
    this.showModal(this.addModalTarget)
  }

  openEditModal(event) {
    if (!this.canEdit()) {
      this.showError('You do not have permission to edit dependents')
      return
    }

    const dependentId = event.currentTarget.dataset.dependentId
    this.loadDependentForEdit(dependentId)
  }

  deleteDependentConfirm(event) {
    if (!this.canDelete()) {
      this.showError('You do not have permission to delete dependents')
      return
    }

    if (confirm('Are you sure you want to remove this dependent?')) {
      this.deleteDependent(event.currentTarget.dataset.dependentId)
    }
  }


  // Private methods
  async loadDependentForEdit(dependentId) {
    try {
      const response = await fetch(`${this.apiBaseUrlValue}/${dependentId}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      this.populateEditForm(data.dependent)
      this.showModal(this.editModalTarget)
    } catch (error) {
      console.error('Error loading dependent:', error)
      this.showError('Failed to load dependent data')
    }
  }

  populateEditForm(dependent) {
    const firstNameField = this.editFormTarget.querySelector('[name="dependent[first_name]"]')
    const lastNameField = this.editFormTarget.querySelector('[name="dependent[last_name]"]')
    const emailField = this.editFormTarget.querySelector('[name="dependent[email]"]')
    const formNameField = this.editFormTarget.querySelector('[data-dependent-form-name]')

    if (firstNameField) firstNameField.value = dependent.first_name || ''
    if (lastNameField) lastNameField.value = dependent.last_name || ''
    if (emailField) emailField.value = dependent.email || ''
    if (formNameField) formNameField.textContent = dependent.form_name || ''

    // Render and populate custom fields
    this.renderCustomFields(dependent)

    // Store dependent ID for form submission
    this.editFormTarget.dataset.dependentId = dependent.id

    this.clearErrors('edit')
  }

  renderCustomFields(dependent) {
    const customFieldsContainer = this.editFormTarget.querySelector('[data-dependent-custom-fields]')
    if (!customFieldsContainer || !dependent.rendered_fields) {
      return
    }

    // Clear existing custom fields
    customFieldsContainer.innerHTML = ''

    // Insert server-rendered fields
    dependent.rendered_fields.forEach(fieldHtml => {
      customFieldsContainer.insertAdjacentHTML('beforeend', fieldHtml)
    })
  }


  async handleAddSubmit(event) {
    event.preventDefault()

    // Validate required fields before submission
    if (!this.validateRequiredFields(this.addFormTarget, 'add')) {
      return
    }

    const formData = new FormData(this.addFormTarget)

    try {
      const response = await fetch(this.apiBaseUrlValue, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        }
      })

      const data = await response.json()

      if (data.success) {
        this.handleAddSuccess(data)
      } else {
        this.handleAddError(data)
      }
    } catch (error) {
      console.error('Error creating dependent:', error)
      this.showFormError('add', ['An error occurred. Please try again.'])
    }
  }

  async handleEditSubmit(event) {
    event.preventDefault()

    // Validate required fields before submission
    if (!this.validateRequiredFields(this.editFormTarget, 'edit')) {
      return
    }

    const dependentId = this.editFormTarget.dataset.dependentId
    const formData = new FormData(this.editFormTarget)

    try {
      const response = await fetch(`${this.apiBaseUrlValue}/${dependentId}`, {
        method: 'PATCH',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        }
      })

      const data = await response.json()

      if (data.success) {
        this.handleEditSuccess(data)
      } else {
        this.handleEditError(data)
      }
    } catch (error) {
      console.error('Error updating dependent:', error)
      this.showFormError('edit', ['An error occurred. Please try again.'])
    }
  }

  async deleteDependent(dependentId) {
    try {
      const response = await fetch(`${this.apiBaseUrlValue}/${dependentId}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        }
      })

      const data = await response.json()

      if (data.success) {
        this.handleDeleteSuccess(dependentId, data.message)
      } else {
        this.showError(data.message || 'Failed to delete dependent')
      }
    } catch (error) {
      console.error('Error deleting dependent:', error)
      this.showError('An error occurred while deleting the dependent')
    }
  }

  handleAddSuccess(data) {
    this.hideModal(this.addModalTarget)
    this.addFormTarget.reset()
    this.clearErrors('add')
    this.showSuccessMessage(data.message)
    this.addDependentToUI(data.dependent)
    this.updateUI()
  }

  handleEditSuccess(data) {
    this.hideModal(this.editModalTarget)
    this.editFormTarget.reset()
    this.clearErrors('edit')
    this.showSuccessMessage(data.message)
    this.updateDependentInUI(data.dependent)
  }

  handleDeleteSuccess(dependentId, message) {
    this.removeDependentFromUI(dependentId)
    this.showSuccessMessage(message)
    this.updateUI()
  }

  handleAddError(data) {
    this.showFormError('add', data.errors || [data.message])
  }

  handleEditError(data) {
    this.showFormError('edit', data.errors || [data.message])
  }

  handleTurboStream(event) {
    const streamElement = event.detail.newStream
    if (streamElement && streamElement.target.startsWith('dependent_')) {
      if (streamElement.action === 'remove') {
        setTimeout(() => {
          this.updateUI()
        }, 100)
      }
    }
  }

  addDependentToUI(dependent) {
    const dependentCard = this.createDependentCard(dependent)

    if (this.hasListTarget) {
      this.listTarget.insertAdjacentHTML('beforeend', dependentCard)
    }

    this.updateDependentsCount(1)
  }

  updateDependentInUI(dependent) {
    const existingCard = document.getElementById(`dependent_${dependent.id}`)
    if (existingCard) {
      const newCard = this.createDependentCard(dependent)
      existingCard.outerHTML = newCard
    }
  }

  removeDependentFromUI(dependentId) {
    const card = document.getElementById(`dependent_${dependentId}`)
    if (card) {
      card.remove()
      this.updateDependentsCount(-1)
    }
  }

  createDependentCard(dependent) {
    const template = document.querySelector('[data-dependent-card-template]')
    if (!template) {
      console.error('Dependent card template not found')
      return ''
    }

    const clone = template.cloneNode(true)
    clone.removeAttribute('data-dependent-card-template')
    clone.style.display = 'block'
    clone.id = `dependent_${dependent.id}`

    // Replace template placeholders
    clone.innerHTML = clone.innerHTML
      .replace(/TEMPLATE_ID/g, dependent.id)
      .replace(/TEMPLATE_FULL_NAME/g, dependent.full_name)
      .replace(/TEMPLATE_FIRST_NAME/g, dependent.first_name)
      .replace(/TEMPLATE_LAST_NAME/g, dependent.last_name)
      .replace(/TEMPLATE_FORM_NAME/g, dependent.form_name)

    // Handle email conditionally
    if (dependent.email) {
      clone.innerHTML = clone.innerHTML
        .replace(/TEMPLATE_EMAIL/g, dependent.email)
        .replace(/TEMPLATE_EMAIL_VERIFIED/g, dependent.email_verified ? 'true' : 'false')
    } else {
      // Remove email elements if no email
      const emailElements = clone.querySelectorAll('[data-email-content]')
      emailElements.forEach(el => el.remove())
    }

    // Handle permissions-based button visibility
    const editButton = clone.querySelector('[data-action="edit"]')
    const deleteButton = clone.querySelector('[data-action="delete"]')

    if (editButton && !this.canEdit()) {
      editButton.style.display = 'none'
    }

    if (deleteButton && !this.canDelete()) {
      deleteButton.style.display = 'none'
    }

    return clone.outerHTML
  }

  updateUI() {
    const dependentCount = this.getDependentCount()

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.style.display = dependentCount === 0 ? 'block' : 'none'
    }

    if (this.hasListTarget) {
      this.listTarget.style.display = dependentCount > 0 ? 'block' : 'none'
    }

    this.updateCountBadge(dependentCount)
  }

  getDependentCount() {
    if (this.hasListTarget) {
      return this.listTarget.querySelectorAll('[id^="dependent_"]').length
    }
    return 0
  }

  updateCountBadge(count) {
    if (this.hasCountBadgeTarget) {
      this.countBadgeTarget.textContent = count
    }
  }

  updateDependentsCount(change) {
    const currentCount = this.getDependentCount()
    const newCount = Math.max(0, currentCount + change)
    this.updateCountBadge(newCount)
  }

  // Permission helpers
  canCreate() {
    return this.permissionsValue.includes('create')
  }

  canEdit() {
    return this.permissionsValue.includes('edit')
  }

  canDelete() {
    return this.permissionsValue.includes('delete')
  }

  hasReachedMaxDependents() {
    if (!this.maxDependentsValue || this.maxDependentsValue <= 0) {
      return false
    }
    return this.getDependentCount() >= this.maxDependentsValue
  }

  // Modal helpers
  showModal(modal) {
    if (modal === this.addModalTarget) {
      this.addModal.show()
    } else if (modal === this.editModalTarget) {
      this.editModal.show()
    }
  }

  hideModal(modal) {
    if (modal === this.addModalTarget) {
      this.addModal.hide()
    } else if (modal === this.editModalTarget) {
      this.editModal.hide()
    }
  }

  // Error handling
  showFormError(formType, errors) {
    const errorContainer = formType === 'add' ? this.addErrorsTarget : this.editErrorsTarget
    const errorList = formType === 'add' ? this.addErrorListTarget : this.editErrorListTarget

    errorList.innerHTML = ''
    errors.forEach(error => {
      const li = document.createElement('li')
      li.textContent = error
      errorList.appendChild(li)
    })
    errorContainer.classList.remove('d-none')
  }

  clearErrors(formType) {
    const errorContainer = formType === 'add' ? this.addErrorsTarget : this.editErrorsTarget
    const errorList = formType === 'add' ? this.addErrorListTarget : this.editErrorListTarget

    if (errorContainer) errorContainer.classList.add('d-none')
    if (errorList) errorList.innerHTML = ''
  }

  showError(message) {
    this.showSuccessMessage(message, 'danger')
  }

  showSuccessMessage(message, type = 'success') {
    const alertDiv = document.createElement('div')
    alertDiv.className = `alert alert-${type} alert-dismissible fade show position-fixed`
    alertDiv.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 300px;'
    alertDiv.innerHTML = `
      ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `

    document.body.appendChild(alertDiv)

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (alertDiv.parentNode) {
        alertDiv.remove()
      }
    }, 5000)
  }

  validateRequiredFields(form, formType = 'add') {
    const requiredFields = form.querySelectorAll('[required]')
    const errors = []

    requiredFields.forEach(field => {
      const label = field.getAttribute('data-label') || field.name

      if (field.type === 'radio') {
        // For radio buttons, check if any option in the group is selected
        const radioGroup = form.querySelectorAll(`[name="${field.name}"]`)
        const isChecked = Array.from(radioGroup).some(radio => radio.checked)
        if (!isChecked) {
          errors.push(`${label} must be selected`)
        }
      } else if (field.type === 'checkbox' && field.hasAttribute('required')) {
        // For required checkboxes
        if (!field.checked) {
          errors.push(`${label} must be checked`)
        }
      } else {
        // For text, email, select, etc.
        if (!field.value || field.value.trim() === '') {
          errors.push(`${label} can't be blank`)
        }
      }
    })

    if (errors.length > 0) {
      this.showFormError(formType, errors)
      return false
    }

    return true
  }

  getCSRFToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
  }
}